<script lang="ts">
import { ThreeEnvironment, threeEnvironmentInjection as threeKey, usePerspectiveCamera, useThree } from '@monogrid/vue-boost/three'
import { templateRef } from '@vueuse/core'
import { ComponentPublicInstance, computed, defineComponent, InjectionKey, provide, Ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { WEBGL_CAMERA_CONFIG } from '@/constants'
import CursorTrail from '@/three/CursorTrail.vue'
import PostFx from '@/three/PostFx.vue'

type WebGLEnvironment = ThreeEnvironment & {
  globals: {
    cursorTrail: Readonly<Ref<ComponentPublicInstance<typeof CursorTrail>>>
    postFx: Readonly<Ref<ComponentPublicInstance<typeof PostFx>>>
  }
}

export const threeEnvironmentInjection: InjectionKey<WebGLEnvironment> = threeKey

export default defineComponent({
  name: 'WebGLApp',
  components: {
    CursorTrail,
    PostFx
  },
  props: {
    rendererConfig: {
      type: Object,
      default: () => ({
        powerPreference: 'high-performance',
        alpha: false,
        antialias: false,
        depth: true,
        stencil: false
      })
    }
  },
  setup (props, { expose }) {
    const canvas = templateRef<HTMLCanvasElement>('canvas')
    const canvasWrapper = templateRef<HTMLElement>('wrapper')
    const cursorTrail = templateRef<ComponentPublicInstance<typeof CursorTrail>>('cursorTrail')
    const postFx = templateRef<ComponentPublicInstance<typeof PostFx>>('postfx')
    const route = useRoute()
    const { environment } = useThree({
      canvas,
      canvasWrapper,
      rendererConfig: () => props.rendererConfig,
      resizeThrottleTime: 100
    }) as { environment: WebGLEnvironment }
    const cameraSettings = computed(() => WEBGL_CAMERA_CONFIG[route.meta.cameraConfig as string || 'DEFAULT'])

    environment.controls = usePerspectiveCamera(() => cameraSettings.value.config)
    environment.globals = {
      cursorTrail,
      postFx
    }

    // function onTick () {
    //   if (!environment.webgl.initialized || !environment.controls) return
    //   environment.webgl.renderer.render(environment.webgl.scene, environment.controls.camera)
    // }

    // useTicker(onTick)

    watch(cameraSettings, ({ position, rotation, lookAt }) => {
      environment.controls.camera.position.set(position.x, position.y, position.z)
      environment.controls.camera.rotation.set(rotation.x, rotation.y, rotation.z)
      environment.controls.camera.lookAt(lookAt.x, lookAt.y, lookAt.z)
    }, { immediate: true })

    watch([postFx, () => route.meta], ([postComponent, meta]) => {
      if (!postComponent) return
      postComponent.currentMode = meta.postfx ? 'fx' : 'standard'
    })

    expose({ environment })
    provide(threeEnvironmentInjection, environment)

    return {}
  }
})
</script>

<template>
  <div ref="wrapper" class="wrapper fixed top-0 left-0 h-lview">
    <canvas ref="canvas" class="absolute inset-0 select-none" />
    <CursorTrail ref="cursorTrail" />
    <PostFx ref="postfx" />
  </div>
  <slot />
</template>

<style lang="scss" scoped>
  .wrapper{
    right: var(--scrollbar-placeholder);
  }
</style>
